import React, {
    createContext,
    useContext,
    useState,
    ReactNode,
    useEffect,
    useMemo,
} from 'react';
import {
    getAccountManagmentSegments,
    getZinantisMembers,
} from '../../services/zinantis/members/zinantisMemberListServices';
import {
    AccountManagementSegmentState,
    MembersFilters,
    MemberState,
    OwnerZinantisAgenciesState,
} from '../../types/zinantis/members/membersType';
import useUserProfile from '../../customHooks/useUserProfile';
import { useNotification } from '../../contexts/NotificationsContext';
import endpoints from '../../utils/commonVariables/endpoints';
import { viewModelEntities } from '../../utils/commonVariables/entityKeys';
import api from '../../services/api';
import { IHotelOwnerAgency } from '../../types/entities/IHotelOwnerAgency';
import { ISupplierHotel } from '../../types/entities/ISupplierHotel';
import { IHotelImage } from '../../types/entities/IHotelImage';
import useTranslate from '../../customHooks/useTranslate';

interface ZinantisMembersContextType {
    accountManagmentSegments: AccountManagementSegmentState[];
    members: MemberState[];
    filters: MembersFilters;
    updateFilters: <K extends keyof MembersFilters>(
        _filterKey: K,
        _value: MembersFilters[K]
    ) => void;
    resetFilters: () => void;
    isZinantisMember: boolean;
    ownerZinantisAgencies: OwnerZinantisAgenciesState[];
    isLoading: boolean;
}

const ContextZinantisMembers = createContext<
    ZinantisMembersContextType | undefined
>(undefined);

const INITIAL_STATE_FILTERS: MembersFilters = {
    name: '',
    idCountry: null,
    connected: 'all',
};

export const ProviderZinantisMembers = ({
    children,
}: {
    children: ReactNode;
}) => {
    const translate = useTranslate();
    const { IdHotelOwner, language } = useUserProfile();
    const { setErrorMessage } = useNotification();

    const [isZinantisMember, setIsZinantisMember] = useState(false);
    const [ownerZinantisAgencies, setOwnerZinantisAgencies] = useState<
        OwnerZinantisAgenciesState[]
    >([]);
    const [accountManagmentSegments, setAccountManagmentSegments] = useState<
        AccountManagementSegmentState[]
    >([]);
    const [members, setMembers] = useState<MemberState[]>([]);
    const [filters, setFilters] = useState<MembersFilters>(
        INITIAL_STATE_FILTERS
    );
    const [isLoading, setIsLoading] = useState(true);

    const updateFilters = <K extends keyof MembersFilters>(
        filterKey: K,
        value: MembersFilters[K]
    ) => {
        setFilters(prevFilters => ({
            ...prevFilters,
            [filterKey]: value,
        }));
    };

    const resetFilters = () => {
        setFilters(INITIAL_STATE_FILTERS);
    };

    useEffect(() => {
        if (!IdHotelOwner) return;

        const loadData = async () => {
            try {
                setIsLoading(true);
                const contract = {
                    ZinantisMembership: true,
                    Agencies: {
                        Id: true,
                        IdAgency: true,
                        ZinantisRelationships: {
                            Id: true,
                            IdHotel: true,
                            Hotel: {
                                Name: true,
                            },
                        },
                    },
                    SupplierHotels: {
                        Id: true,
                        Name: true,
                        IdHotel: true,
                        Hotel: {
                            Name: true,
                        },
                        SupplierHotelImages: {
                            Id: true,
                            VirtualPath: true,
                            Feature: true,
                        },
                    },
                };
                const endpoint = endpoints.viewModel(
                    viewModelEntities.HotelOwner,
                    IdHotelOwner
                );
                const { data } = await api.post(endpoint, contract);
                const { ZinantisMembership, Agencies, SupplierHotels } =
                    data.Result;
                setIsZinantisMember(ZinantisMembership);

                if (ZinantisMembership) {
                    const zinantisAgencies: OwnerZinantisAgenciesState[] =
                        Agencies.map((agency: IHotelOwnerAgency) => {
                            return {
                                id: agency.Id,
                                idAgency: agency.IdAgency,
                                hotels: agency.ZinantisRelationships.filter(
                                    relationship =>
                                        SupplierHotels.some(
                                            (supHot: ISupplierHotel) =>
                                                supHot.IdHotel ===
                                                relationship.IdHotel
                                        )
                                ).map(relationship => {
                                    const supHotel = SupplierHotels.find(
                                        (supHot: ISupplierHotel) =>
                                            supHot.IdHotel ===
                                            relationship.IdHotel
                                    );

                                    const image =
                                        supHotel?.SupplierHotelImages?.find(
                                            (img: IHotelImage) => img.Feature
                                        ) || supHotel?.SupplierHotelImages?.[0];

                                    return {
                                        id: relationship.Id,
                                        idHotel: relationship.IdHotel,
                                        hotelName: supHotel.Name || '',
                                        idSupplierHotel:
                                            parseInt(supHotel?.Id) || 0,
                                        image: image.VirtualPath || '',
                                    };
                                }),
                            };
                        });
                    setOwnerZinantisAgencies(zinantisAgencies);

                    const [segments, membersDDBB] = await Promise.all([
                        getAccountManagmentSegments(),
                        getZinantisMembers(),
                    ]);
                    setAccountManagmentSegments(segments);
                    const connectedMembers = membersDDBB.map(member => {
                        const agency = zinantisAgencies.find(zinantisAgency => {
                            return (
                                zinantisAgency.idAgency === parseInt(member.id)
                            );
                        });

                        return {
                            ...member,
                            connected: Boolean(agency),
                            hotels: agency ? agency.hotels : [],
                        };
                    });
                    setMembers(connectedMembers);
                }

                setIsLoading(false);
            } catch (error) {
                console.error(error);
                setErrorMessage(translate('something_went_wrong'));
            }
        };

        loadData();
    }, [IdHotelOwner, language]);

    return (
        <ContextZinantisMembers.Provider
            value={{
                accountManagmentSegments,
                members,
                filters,
                updateFilters,
                resetFilters,
                isZinantisMember,
                ownerZinantisAgencies,
                isLoading,
            }}
        >
            {children}
        </ContextZinantisMembers.Provider>
    );
};

export const useZinantisMembers = () => {
    const context = useContext(ContextZinantisMembers);
    if (!context) {
        throw new Error(
            'useZinantisMembers debe usarse dentro de un ZinantisMembersProvider'
        );
    }

    const { members, filters, accountManagmentSegments } = context;

    const getFilteredMembers = (_members: MemberState[]) => {
        let filtered = [..._members];

        if (filters.idCountry) {
            filtered = filtered.filter(
                member => member.idCountry === filters.idCountry
            );
        }
        if (filters.name) {
            filtered = filtered.filter(member =>
                member?.name
                    ?.toLocaleLowerCase()
                    .includes(filters.name.toLowerCase())
            );
        }
        if (filters.connected !== 'all') {
            filtered = filtered.filter(member => {
                const filterValue = filters.connected === 'connected';
                return member.connected === filterValue;
            });
        }
        return filtered;
    };

    const membersWithoutSegment = useMemo(() => {
        const validSegmentIds = accountManagmentSegments.map(s =>
            parseInt(s.id)
        );

        return members.filter(
            member =>
                !validSegmentIds.includes(
                    Number(member.idAccountManagmentSegment)
                ) &&
                member?.name
                    ?.toLocaleLowerCase()
                    .includes(filters.name.toLowerCase())
        );
    }, [members, accountManagmentSegments, filters]);

    const connectedMembers = useMemo(() => {
        return members.filter(member => member.connected);
    }, [members]);

    const newMembers = useMemo(() => {
        return members.filter(member => member.isNew);
    }, [members]);

    return {
        ...context,
        filteredMembers: getFilteredMembers(members),
        membersWithoutSegment,
        connectedMembers,
        newMembers,
        getFilteredMembers,
    };
};
